import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }
    return context;
};

export const WebSocketProvider = ({ children }) => {
    const [connected, setConnected] = useState(false);
    const [lastMessage, setLastMessage] = useState(null);
    const socketRef = useRef(null);
    const listenersRef = useRef(new Map());

    const connect = useCallback(() => {
        if (socketRef.current?.connected) return;

        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
        
        // Generate a unique client ID
        const clientId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // For Socket.IO, we use the HTTP URL
        socketRef.current = io(backendUrl, {
            transports: ['websocket', 'polling'],
            path: '/socket.io',
            autoConnect: true,
        });

        socketRef.current.on('connect', () => {
            console.log('WebSocket connected');
            setConnected(true);
        });

        socketRef.current.on('disconnect', () => {
            console.log('WebSocket disconnected');
            setConnected(false);
        });

        socketRef.current.on('message', (data) => {
            setLastMessage(data);
            // Notify all listeners
            listenersRef.current.forEach((callback) => {
                callback(data);
            });
        });

        socketRef.current.on('game_update', (data) => {
            setLastMessage(data);
            listenersRef.current.forEach((callback) => {
                callback(data);
            });
        });

    }, []);

    const disconnect = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
            setConnected(false);
        }
    }, []);

    const subscribe = useCallback((id, callback) => {
        listenersRef.current.set(id, callback);
        return () => {
            listenersRef.current.delete(id);
        };
    }, []);

    const sendMessage = useCallback((type, data) => {
        if (socketRef.current?.connected) {
            socketRef.current.emit(type, data);
        }
    }, []);

    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [disconnect]);

    const value = {
        connected,
        lastMessage,
        connect,
        disconnect,
        subscribe,
        sendMessage,
    };

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
};

export default WebSocketContext;
